import { Component, Inject, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Evaluation, EvaluationStatus, ImageTransferResult, Sample, SampleService, SampleType } from '@core/data';
import { Dialog, Snackbar } from '@core/material';
import { TransferImages } from './image-move.state-actions';
import { ImageMoveImageType } from './image-move.state-model';
import { ImageMoveState } from './image-move.state';
import { ImageMoveWarningDialog, ImageMoveWarningDialogData } from './image-move-warning.dialog';
import { ImageMoveSamplePickerDialog, ImageMoveSamplePickerDialogData } from './image-move-sample-picker.dialog';
import { GetEvaluationStatusPipe } from '../../pipes/get-evaluation-status.pipe';

export interface ImageMoveDestinationPickerDialogData {
    sampleKey: string;
    sourceEvalKey: string;
    imageKeys: string[];
    evaluations: Evaluation[];
    orgKey: string;
    sampleType: SampleType;
    // Optional for backward compatibility with existing test fixtures - real call
    // sites always pass this explicitly. Undefined is treated as 'sample'.
    imageType?: ImageMoveImageType;
}

export interface ImageMoveDestinationPickerResult {
    destinationEvalKey: string;
    destinationLabel: string;
    results: ImageTransferResult[];
}

@Component({
    selector: 'pv-image-move-destination-picker-dialog',
    templateUrl: 'image-move-destination-picker.dialog.html',
    styleUrls: ['image-move-destination-picker.dialog.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
})
export class ImageMoveDestinationPickerDialog implements OnDestroy {

    working = false;

    // The escape-hatch picker replaces the row list with a different Sample's
    // Evaluations - kept separate from `data.evaluations` so "Change sample"
    // (see template) can always re-derive from a clean slate.
    crossSampleMode = false;
    evaluations: Evaluation[] = [];
    destinationSampleLabel: string;

    // Guards against opening a second fullscreen picker (or evaluation-fetch) while
    // one is already in flight - a rapid double-click would otherwise stack dialogs.
    private _pickingSample = false;

    // Cancels an in-flight loadEvaluationsForSample() fetch whenever a newer pick
    // supersedes it, so an earlier, slower response can't overwrite a later one.
    private _cancelPick$ = new Subject<void>();

    private _destroy$ = new Subject<void>();

    // Angular templates cannot use a pipe inside an event-binding expression, so the
    // row-click handler resolves status itself rather than accepting it from the template.
    private _statusPipe = new GetEvaluationStatusPipe();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: ImageMoveDestinationPickerDialogData,
        private _store: Store,
        private _dialog: Dialog,
        private _snackbar: Snackbar,
        private _ref: MatDialogRef<ImageMoveDestinationPickerDialog>,
        private _sampleService: SampleService,
        private _changeDetectorRef: ChangeDetectorRef,
    ) { }

    trackEvaluation(index, item: Evaluation) {
        return item.key;
    }

    openSamplePicker() {

        if (this.working || this._pickingSample) return;

        this._pickingSample = true;

        const data: ImageMoveSamplePickerDialogData = {
            orgKey: this.data.orgKey,
            sampleType: this.data.sampleType,
            excludeSampleKey: this.data.sampleKey,
        };

        this._dialog.openFullscreen(ImageMoveSamplePickerDialog, { data })
            .afterClosed()
            .pipe(takeUntil(this._destroy$))
            .subscribe((sample: Sample) => {
                this._pickingSample = false;
                if (!sample) {
                    // Cancelling the fullscreen sample browser exits the whole Move flow
                    // rather than stranding the user on this half-configured picker.
                    this._ref.close(null);
                    return;
                }
                this.loadEvaluationsForSample(sample);
            });
    }

    selectDestination(evalu: Evaluation) {

        if (this.working) return;

        const status = this._statusPipe.transform(evalu);

        if (status === EvaluationStatus.COMPLETE) {
            // Complete always wins the confirm slot, cross-sample or not - its own
            // copy already implies the destination is a completed evaluation.
            this.confirmAndTransfer(evalu, this.completeWarningData(evalu));
            return;
        }

        if (this.crossSampleMode) {
            this.confirmAndTransfer(evalu, this.crossSampleConfirmData(evalu));
            return;
        }

        this.transfer(evalu); // same-sample, non-Complete: implicit confirm, unchanged from Story 2.2
    }

    cancel() {
        this._ref.close(null);
    }

    private loadEvaluationsForSample(sample: Sample) {

        // A newer pick supersedes whatever fetch is still in flight.
        this._cancelPick$.next();

        this._sampleService.get(sample.key, { related: ['evals'] })
            .pipe(takeUntil(this._cancelPick$), takeUntil(this._destroy$))
            .subscribe({
                next: (result) => {
                    this.crossSampleMode = true;
                    this.destinationSampleLabel = sample.label || 'Unlabeled';
                    this.evaluations = result.evals || [];
                    this._changeDetectorRef.markForCheck();
                },
                error: () => {
                    this._snackbar.error('Could not load that sample\'s evaluations. Please try again.');
                    // crossSampleMode is NOT set - the dialog stays on its current (same-sample) list
                    this._changeDetectorRef.markForCheck();
                },
            });
    }

    private confirmAndTransfer(evalu: Evaluation, warningData: ImageMoveWarningDialogData) {
        this._dialog.open(ImageMoveWarningDialog, { data: warningData })
            .afterClosed()
            .pipe(takeUntil(this._destroy$))
            .subscribe(confirmed => {
                if (confirmed) this.transfer(evalu);
            });
    }

    private completeWarningData(evalu: Evaluation): ImageMoveWarningDialogData {
        return {
            title: 'Moving into a completed evaluation',
            body: `'${evalu.label || 'Unlabeled'}' is marked complete. You can still move images into it, but double check this is the evaluation you meant.`,
            confirmLabel: 'Move anyway',
        };
    }

    private crossSampleConfirmData(evalu: Evaluation): ImageMoveWarningDialogData {
        const count = this.data.imageKeys.length;
        return {
            title: 'Confirm move',
            body: `Move ${count} image${count === 1 ? '' : 's'} to '${this.destinationSampleLabel}' → '${evalu.label || 'Unlabeled'}'? This moves to a different sample.`,
            confirmLabel: 'Move',
            cancelLabel: 'Cancel',
        };
    }

    private transfer(evalu: Evaluation) {

        this.working = true;

        this._store.dispatch(new TransferImages(this.data.imageKeys, evalu.key, this.data.imageType || 'sample'))
            .subscribe({
                next: () => {

                    const results = this._store.selectSnapshot(ImageMoveState.transferResults);

                    const result: ImageMoveDestinationPickerResult = {
                        destinationEvalKey: evalu.key,
                        destinationLabel: evalu.label || 'Unlabeled',
                        results: this.data.imageKeys
                            .map(key => results[key])
                            .filter(r => !!r),
                    };

                    this._ref.close(result);
                },
                error: () => {
                    this.working = false;
                    this._snackbar.error('Move failed. Please try again.');
                },
            });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        this._cancelPick$.complete();
    }
}
